import type {
  Accounts,
  Groups,
  GroupMappedAccounts,
  MappedAccountIds,
  Group,
  Account,
  LocalisedNames,
  CapitalisedLocale,
  AIRecommendation,
} from '../../shared-types'
import type { MappingSource } from '../../constants'
import { MAPPING_SOURCES } from '../../constants'
import { getAccountRangeAndPostfix } from './account-numbers'

export type AccountSavePayload = Pick<
  Account,
  'id' | 'number' | 'accountType'
> &
  LocalisedNames & {
    mappingSource: MappingSource
  }

export type FormatRequestDataOptions = {
  accounts: Accounts
  groups: Groups
  groupMappedAccounts: GroupMappedAccounts
  mappedAccountIds: MappedAccountIds
  shouldUseOriginalNames: boolean
  supportedLocales: CapitalisedLocale[]
  recommendations?: Record<string, AIRecommendation>
}
export type BuildRequestItemOptions = {
  account: Account
  group: Group
  id: string
  shouldUseOriginalNames: boolean
  mappedNumber: string
  supportedLocales: CapitalisedLocale[]
  recommendation?: AIRecommendation
}

export const buildRequestItem = ({
  id,
  mappedNumber,
  account,
  group,
  shouldUseOriginalNames,
  supportedLocales,
  recommendation,
}: BuildRequestItemOptions): AccountSavePayload => {
  const localisedNameFields = supportedLocales.reduce(
    (acc, locale) => ({
      ...acc,
      [`name${locale}`]: shouldUseOriginalNames
        ? account[`originalName${locale}`]
        : group[`name${locale}`],
    }),
    {} as Record<keyof LocalisedNames, string>
  )

  // Extract base account range from mappedNumber (e.g., '100000.001' -> '100000')
  const [baseAccountRange] = getAccountRangeAndPostfix(mappedNumber)
  const effectiveAccountRange = baseAccountRange || mappedNumber

  const matchingProposal = recommendation?.proposals.find(
    (p) => p.accountRange === effectiveAccountRange
  )
  const mappingSource = matchingProposal?.source ?? MAPPING_SOURCES.MANUAL

  return {
    id,
    number: mappedNumber,
    accountType: group.type,
    mappingSource,
    ...localisedNameFields,
  }
}
